/**
 * EmergeBadge — the cyan-to-blue "emerge" wordmark stamped on every
 * BLK PACKS collectible. Signals ledger-verification + doubles as a
 * one-tap share surface.
 *
 * Behaviour on tap (stopPropagation so card clicks never fire):
 *   1. If Web Share API is available (iOS/Android/modern browsers)
 *      → native share sheet with pre-filled title+url (lets user
 *        send to X / IG Stories / Messages / Mail / etc.).
 *   2. Otherwise → small inline popover with Copy Link / Post to X
 *      / View on EMERGE, plus a "Copied" toast via setCopied state.
 *
 * Sizes: xs (10px) · sm (14px) · md (20px) · lg (28px).
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Copy, Twitter, ExternalLink, Share2, Check } from "lucide-react";
import { getShareOrigin } from "../../utils";

const SIZE = {
  xs: { h: 10, letter: "-0.02em" },
  sm: { h: 14, letter: "-0.025em" },
  md: { h: 20, letter: "-0.028em" },
  lg: { h: 28, letter: "-0.03em" },
};

const _buildLedgerUrl = (verificationId) => {
  if (typeof window === "undefined") return "";
  // Always use the canonical brand origin so share links never leak the
  // underlying emergent.host alias into iMessage / X / IG link previews.
  return `${getShareOrigin()}/verify/${encodeURIComponent(verificationId)}`;
};

export default function EmergeBadge({
  size = "sm",
  verificationId,
  dropId,
  showDot = true,
  className = "",
  title = "Verified on EMERGE · tap to share",
  cardName,
}) {
  const cfg = SIZE[size] || SIZE.sm;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const popRef = useRef(null);

  // Close popover on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const shareUrl = verificationId ? _buildLedgerUrl(verificationId) : "";
  const shareText = cardName
    ? `${cardName} — verified on EMERGE public ledger`
    : "Verified on EMERGE · BLK PACKS public ledger";

  // Fire-and-forget share tracking to /api/verify/share — never blocks the share UX.
  const _trackShare = useCallback((channel) => {
    if (!verificationId) return;
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      // keepalive so the request survives navigation (tapping Post on X, etc.)
      fetch(`${API}/verify/share/${encodeURIComponent(verificationId)}?channel=${channel}`, {
        method: "POST",
        keepalive: true,
      }).catch(() => {});
    } catch { /* noop */ }
  }, [verificationId]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      _trackShare("copy");
      setTimeout(() => setCopied(false), 1400);
    } catch { /* noop */ }
  }, [shareUrl, _trackShare]);

  const handleClick = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!verificationId) return;

    // Try native Web Share first (iOS Safari, Android Chrome,
    // Capacitor Share plugin bridge). This is the one-tap path that
    // surfaces IG Stories / X / Messages etc. as native targets.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: cardName ? `${cardName} · EMERGE` : "EMERGE · BLK PACKS",
          text: shareText,
          url: shareUrl,
        });
        _trackShare("native");
        return;
      } catch (err) {
        // User cancelled or share failed — fall through to popover.
        if (err && err.name === "AbortError") return;
      }
    }

    // Desktop / unsupported browsers → show inline popover.
    setOpen((v) => !v);
  }, [verificationId, cardName, shareText, shareUrl, _trackShare]);

  const wordmark = (
    <span
      className="inline-flex items-center gap-1 brand-gradient-text lowercase font-bold leading-none"
      style={{
        fontFamily: "'Comfortaa', 'Quicksand', sans-serif",
        letterSpacing: cfg.letter,
        fontSize: `${cfg.h}px`,
        paddingBottom: "2px",
      }}
    >
      emerge
    </span>
  );

  const body = (
    <span
      title={verificationId ? title : "Indexed on EMERGE public ledger"}
      className={`inline-flex items-center gap-1 select-none ${className}`}
      data-testid="emerge-badge"
    >
      {showDot && (
        <span className="relative flex items-center">
          <span className="absolute inline-flex h-1.5 w-1.5 rounded-full bg-amber-300/60 animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-300" />
        </span>
      )}
      {wordmark}
    </span>
  );

  // Non-interactive (no verification_id AND no dropId): just display the wordmark.
  if (!verificationId && !dropId) return body;

  // Drop-level fallback: no per-card verification_id yet (e.g. sealed pack tiles
  // on the /drops list) — link straight to the drop's public EMERGE ledger view.
  // We use a plain <Link> so browsers still get proper right-click / open-in-tab.
  if (!verificationId && dropId) {
    return (
      <Link
        to={`/emerge/drop/${encodeURIComponent(dropId)}`}
        onClick={(e) => e.stopPropagation()}
        className="hover:brightness-125 active:scale-95 transition-all cursor-pointer inline-flex"
        data-testid={`emerge-badge-drop-link-${dropId}`}
        aria-label="View drop on EMERGE ledger"
        title="View this drop on the EMERGE public ledger"
      >
        {body}
      </Link>
    );
  }

  return (
    <span ref={popRef} className="relative inline-flex" data-testid="emerge-badge-root">
      <button
        type="button"
        onClick={handleClick}
        className="hover:brightness-125 active:scale-95 transition-all cursor-pointer bg-transparent p-0 border-0"
        data-testid="emerge-badge-share-btn"
        aria-label="Share EMERGE ledger link"
      >
        {body}
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute z-50 bottom-full right-0 mb-2 min-w-[220px] bg-[#0a0a14]/95 backdrop-blur-md border border-amber-500/30 rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.6)] overflow-hidden"
          data-testid="emerge-share-popover"
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace" }}
        >
          <div className="px-3 py-2 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-amber-300/80 flex items-center gap-1.5">
            <Share2 size={10} /> Share ledger link
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyLink(); }}
            className="w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-amber-500/10 text-white/80 hover:text-amber-200 text-xs transition-colors"
            data-testid="emerge-share-copy"
          >
            {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
            <span className="flex-1">{copied ? "Copied to clipboard" : "Copy ledger link"}</span>
          </button>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.stopPropagation(); _trackShare("twitter"); setOpen(false); }}
            className="w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-amber-500/10 text-white/80 hover:text-amber-200 text-xs transition-colors"
            data-testid="emerge-share-twitter"
          >
            <Twitter size={13} />
            <span className="flex-1">Post on X</span>
            <ExternalLink size={10} className="opacity-40" />
          </a>
          <Link
            to={`/verify/${encodeURIComponent(verificationId)}`}
            onClick={(e) => { e.stopPropagation(); _trackShare("view"); setOpen(false); }}
            className="w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-amber-500/10 text-white/80 hover:text-amber-200 text-xs transition-colors border-t border-white/5"
            data-testid="emerge-share-view"
          >
            <ExternalLink size={13} />
            <span className="flex-1">View on EMERGE</span>
          </Link>
          <div className="px-3 py-1.5 border-t border-white/5 text-[9px] text-white/30 truncate">
            {verificationId}
          </div>
        </div>
      )}
    </span>
  );
}
